import {
  Autocomplete,
  AutocompleteCloseReason,
  Avatar,
  Box,
  FilterOptionsState,
  InputBaseComponentProps,
  InputLabelProps,
  Paper,
  SxProps,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { DEFAULT_AUTOCOMPLETE_RESULT_LIMIT } from 'Config';
import { useIsDesktop } from 'Hooks';
import { BlueX } from 'Images';
import React, {
  Dispatch,
  HTMLAttributes,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { CheckboxList } from './CheckboxList';
import { ResetFilterButtons } from 'Modules/Suppliers/SuppliersMain/ResultsPages/Header/DynamicAdvancedFilter/ResetFilterButtons';

interface IControlledAutocomplete {
  value?: string;
  setValue: Dispatch<SetStateAction<string | undefined | null>> | Function;
  inputValue?: string;
  setInputValue?: (value: string) => void;
  options: string[];
  label: string;
  noResultsText?: string;
  showClearButton?: boolean;
  inputType?: string;
  disableClearIcon?: boolean;
  style?: SxProps;
  endAdornment?: React.ReactNode;
  endAdornmentActiveTransformations?: string;
  renderOptionsProps?: SxProps;
  paperSx?: SxProps;
  paperComponent?: React.ReactNode;
  resultLimit?: number;
  shrink?: boolean;
  inputLabelProps?: Partial<InputLabelProps>;
  freeSolo?: boolean;
  inputProps?: InputBaseComponentProps;
  filterOptions?: (
    options: string[],
    state: FilterOptionsState<string>,
  ) => string[];
  isSetDataOnInputChange?: boolean;
  placeholder?: string;
  isCheckbox?: boolean;
  selectedCheckboxes?: string[];
  onClickCheckbox?: (value: string) => void;
  resetSelectedCheckboxes?: () => void;
  scrollPaperResultHandler?: (event: React.UIEvent<HTMLDivElement>) => void;
  listRef?: any;
  error?: boolean;
  helperText?: React.ReactNode;
  ariaLabeledBy?: string;
}

export const ControlledAutocomplete = ({
  value,
  setValue,
  inputValue,
  setInputValue,
  options,
  label,
  noResultsText,
  showClearButton = true,
  inputType,
  style,
  endAdornment,
  endAdornmentActiveTransformations,
  renderOptionsProps,
  paperSx,
  paperComponent,
  disableClearIcon,
  resultLimit,
  inputLabelProps,
  freeSolo,
  inputProps,
  filterOptions,
  isSetDataOnInputChange,
  placeholder,
  isCheckbox,
  selectedCheckboxes,
  onClickCheckbox,
  resetSelectedCheckboxes,
  scrollPaperResultHandler,
  listRef,
  error,
  helperText,
  ariaLabeledBy,
}: IControlledAutocomplete) => {
  const isDesktop = useIsDesktop();
  const [open, setOpen] = useState(false);

  const autoC = useRef<any>(undefined);
  const popperRef = useRef<any>(undefined);

  // Clear Value solution for AutoComplete problem.
  useEffect(() => {
    if (!value && autoC) {
      const element = autoC?.current.getElementsByClassName(
        'MuiAutocomplete-clearIndicator',
      )[0];
      element?.click();
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popperRef.current &&
        !popperRef.current.contains(event.target) &&
        !autoC.current.contains(event.target) &&
        event.target !== autoC.current.querySelector('input')
      ) {
        setOpen(false);
      }
    };

    if (paperComponent) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [popperRef, autoC, paperComponent]);

  const handleOnClose = (
    event: React.SyntheticEvent<Element, Event>,
    reason: AutocompleteCloseReason,
  ) => {
    if (reason === 'selectOption' && isCheckbox) return;
    if (paperComponent) {
      if (reason !== 'blur') setOpen(false);
    } else setOpen(false);
  };

  return (
    <Autocomplete
      filterOptions={
        filterOptions
          ? filterOptions
          : createFilterOptions({
              limit: resultLimit || DEFAULT_AUTOCOMPLETE_RESULT_LIMIT,
            })
      }
      freeSolo={freeSolo}
      ref={autoC}
      disablePortal
      blurOnSelect={'touch'}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={handleOnClose}
      onKeyUp={(e) => {
        if (e.key === 'Escape') setOpen(false);
      }}
      onSelect={() => {
        paperComponent && setOpen(true);
      }}
      popupIcon={<></>}
      aria-labelledby={ariaLabeledBy}
      sx={{
        width: '100%',
        maxWidth: isDesktop ? '450px' : '100%',
        m: isDesktop ? undefined : '0 auto',
        borderRadius: '8px',
        bgcolor: '#fff',
        '&&& .MuiAutocomplete-inputRoot.MuiOutlinedInput-root': {
          maxHeight: '46px',
        },
        '&&& .MuiOutlinedInput-notchedOutline': {
          borderRadius: '8px',
          borderColor: 'primary.shade_200',
          borderWidth: '1px',
          direction: 'ltr',
          '& legend': {
            textAlign: 'right',
            direction: 'rtl',
            '& span': {
              paddingLeft: '5px',
              paddingRight: '5px',
            },
          },
        },
        '&&& .MuiInputBase-root input[dropdown="open"] ~ fieldset': {
          borderColor: 'primary.main',
          borderRadius: '8px',
          borderWidth: '1px',
        },
        '&&& .MuiInputBase-root input[dropdown="closed"] ~ fieldset': {
          border: '1px solid',
          borderColor: 'primary.shade_200',
        },

        '&&& .MuiInputLabel-root': {
          left: 'auto',
          right: 0,
          transformOrigin: 'top right',
          textAlign: 'right',
          mr: '25px',
        },
        '&&& .MuiInputLabel-root.MuiInputLabel-shrink': {
          mr: 0,
          right: 0,
        },
        '&&& .MuiOutlinedInput-root': {
          height: isDesktop ? '48px' : '42px',
          pl: '12px',
        },
        '&&& .MuiOutlinedInput-input': {
          p: '0px',
        },

        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: error ? '#d32f2f !important' : 'inherit',
            borderWidth: error ? '1px' : '1px',
          },
          '&:hover fieldset': {
            borderColor: error ? '#d32f2f !important' : 'inherit',
          },
          '&.Mui-focused fieldset': {
            borderColor: error ? '#d32f2f !important' : 'inherit',
            borderWidth: error ? '1px' : '1px',
          },
        },

        ...style,
      }}
      clearIcon={
        disableClearIcon ? null : (
          <Avatar
            alt=''
            src={BlueX}
            sx={{
              width: '25px',
              height: '25px',
              visibility: showClearButton ? 'visible' : 'hidden',
            }}
          />
        )
      }
      options={(options || []).sort(
        isCheckbox
          ? (a, b) => {
              const aIsSelected = selectedCheckboxes?.includes(a);
              const bIsSelected = selectedCheckboxes?.includes(b);
              if (aIsSelected && !bIsSelected) return -1;
              if (!aIsSelected && bIsSelected) return 1;
              return a.localeCompare(b);
            }
          : (a, b) => (a > b ? 1 : -1),
      )}
      noOptionsText={<Typography role='alert'>{noResultsText}</Typography>}
      value={value}
      onChange={(__: any, newValue: any) => {
        setValue(newValue);
      }}
      inputValue={inputValue}
      onInputChange={(__: any, newInputValue: string) => {
        setTimeout(() => {
          const element = document.querySelector('.MuiAutocomplete-noOptions');
          if (element) element.setAttribute('role', 'alert');
        }, 0);

        if (isSetDataOnInputChange) setValue(newInputValue);
        if (setInputValue) setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          placeholder={placeholder}
          onClick={() => setOpen(true)}
          {...params}
          size='small'
          label={label}
          aria-label={label}
          type={inputType ? inputType : undefined}
          error={error}
          helperText={helperText}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: endAdornment,
              sx: {
                img: {
                  transform:
                    open && endAdornment && endAdornmentActiveTransformations
                      ? endAdornmentActiveTransformations
                      : undefined,
                },
              },
            },

            htmlInput: {
              ...params.inputProps,
              dropdown: open ? 'open' : 'closed',
              'aria-label': label || placeholder,
              ...inputProps,
            },

            inputLabel: {
              ...inputLabelProps,
            },

            formHelperText: {
              sx: {
                margin: '3px 0 0 0',
                lineHeight: '1.25',
                color: error ? '#d32f2f' : 'inherit',
              },
            },
          }}
        />
      )}
      renderOption={
        isCheckbox
          ? (props, option: string) => {
              return (
                <>
                  <CheckboxList
                    key={option}
                    props={props}
                    option={option}
                    isSelected={
                      selectedCheckboxes
                        ? selectedCheckboxes.includes(option)
                        : false
                    }
                    onClickCheckbox={onClickCheckbox}
                  />
                  {option === options[options.length - 1] &&
                    resetSelectedCheckboxes && (
                      <ResetFilterButtons
                        resetCallback={() => resetSelectedCheckboxes()}
                        sx={{
                          width: '295px',
                          bottom: '1px',
                          p: '6px 15px 6px 0px',
                          justifyContent: 'flex-start',
                          borderRadius: '0px 0px 12px',
                        }}
                      />
                    )}
                </>
              );
            }
          : (props: HTMLAttributes<HTMLLIElement>, option: string) => (
              <Typography
                {...props}
                sx={{
                  p: '13px 20px 13px 0px !important',
                  '&&&:hover': {
                    cursor: 'pointer',
                    fontWeight: 700,
                    bgcolor: 'primary.shade_100',
                  },
                  ...renderOptionsProps,
                }}
              >
                {option}
              </Typography>
            )
      }
      slots={{
        paper: paperComponent
          ? (props) => (
              <Box
                {...props}
                ref={popperRef}
                sx={{
                  maxHeight: '200px',
                  padding: '14px',
                  borderRadius: '0 8px 8px 0px',
                  boxShadow: '0px 4px 24px rgba(45, 112, 217, 0.16)',
                  bgcolor: '#fff',
                  color: 'text.secondary',
                  fontWeight: 400,
                  ...paperSx,
                }}
                onKeyUp={(e) => {
                  if (e.key === 'Escape') setOpen(false);
                }}
              >
                {paperComponent}
                <Box ref={listRef} onScrollCapture={scrollPaperResultHandler}>
                  {props.children}
                </Box>
                {resetSelectedCheckboxes && (
                  <ResetFilterButtons
                    resetCallback={() => resetSelectedCheckboxes()}
                    sx={{
                      width: '295px',
                      bottom: '1px',
                      p: '6px 15px 6px 0px',
                      justifyContent: 'flex-start',
                      borderRadius: '0px 0px 12px',
                    }}
                  />
                )}
              </Box>
            )
          : (props) => (
              <Paper
                {...props}
                sx={{
                  maxHeight: '200px',
                  borderRadius: '0 8px 8px 0px',
                  boxShadow: '0px 4px 24px rgba(45, 112, 217, 0.16)',
                  color: 'text.secondary',
                  fontWeight: 400,

                  '&&& .Mui-focused': {
                    fontWeight: 700,
                    bgcolor: 'primary.shade_100',
                  },

                  '& .MuiAutocomplete-listbox': {
                    maxHeight: '200px',
                    p: 0,
                  },
                  ...paperSx,
                }}
                onScrollCapture={scrollPaperResultHandler}
                ref={listRef}
              />
            ),
      }}
    />
  );
};
